import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { locations, primaryLocation } from "@/lib/locations-data";
import { siteConfig } from "@/lib/site-config";

const LocationMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Record<string, L.Marker>>({});
  const [activeSlug, setActiveSlug] = useState<string>(primaryLocation.slug);

  useEffect(() => {
    let cancelled = false;

    const initMap = async () => {
      // Load the leaflet stylesheet and library only when the map is needed.
      await import("leaflet/dist/leaflet.css");
      const L = await import("leaflet");

      if (cancelled) return;

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }

      if (!mapRef.current) return;

      const map = L.map(mapRef.current, {
        zoomControl: true,
        scrollWheelZoom: false,
      }).setView([primaryLocation.lat, primaryLocation.lng], 9);

      mapInstanceRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      // 30-mile radius circle (48,280 meters)
      L.circle([primaryLocation.lat, primaryLocation.lng], {
        radius: 48280,
        color: "hsl(36, 92%, 32%)",
        fillColor: "hsl(36, 92%, 32%)",
        fillOpacity: 0.08,
        weight: 2,
        dashArray: "8 4",
      }).addTo(map);

      const primaryIcon = L.divIcon({
        className: "custom-marker-primary",
        html: `<div style="background:hsl(24,20%,14%);width:36px;height:36px;border-radius:50%;border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
        </div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });

      const cityIcon = L.divIcon({
        className: "custom-marker-city",
        html: `<div style="background:hsl(36,92%,32%);width:20px;height:20px;border-radius:50%;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.25);"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      locations.forEach((loc) => {
        const icon = loc.isPrimary ? primaryIcon : cityIcon;
        const marker = L.marker([loc.lat, loc.lng], { icon })
          .addTo(map)
          .bindPopup(
            `<div style="padding:8px;font-family:sans-serif;min-width:180px;">
              <strong style="font-size:14px;color:hsl(24,20%,14%);">${loc.name}, ${loc.state}</strong>
              <p style="color:#666;font-size:12px;margin:4px 0 6px;">${loc.region}${loc.isPrimary ? " · HQ" : ""}</p>
              <a href="/locations/${loc.slug}" style="color:hsl(36,92%,32%);font-weight:600;font-size:12px;text-decoration:none;">View location →</a>
            </div>`
          );
        markersRef.current[loc.slug] = marker;
      });
    };

    // Defer map creation (leaflet + OpenStreetMap tiles) until it scrolls near.
    const el = mapRef.current;
    let io: IntersectionObserver | undefined;
    if (el) {
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io?.disconnect();
            initMap();
          }
        },
        { rootMargin: "400px 0px" }
      );
      io.observe(el);
    }

    return () => {
      cancelled = true;
      io?.disconnect();
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const handleLocationClick = (slug: string, lat: number, lng: number) => {
    setActiveSlug(slug);
    if (mapInstanceRef.current) {
      const zoom = slug === primaryLocation.slug ? 10 : 12;
      mapInstanceRef.current.flyTo([lat, lng], zoom, { duration: 1 });
      setTimeout(() => {
        markersRef.current[slug]?.openPopup();
      }, 800);
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-border shadow-lg bg-card">
      <div className="bg-primary px-5 py-3 flex items-center gap-2">
        <MapPin className="h-4 w-4 text-primary-foreground" />
        <span className="font-heading font-bold text-primary-foreground text-sm tracking-wide">
          Service Area — {siteConfig.address.serviceRadius} Radius from {siteConfig.address.city},{" "}
          {siteConfig.address.state}
        </span>
      </div>

      <div className="flex flex-col lg:flex-row">
        <div className="w-full lg:w-[35%] lg:border-r border-border overflow-y-auto max-h-[350px] lg:max-h-[520px] scrollbar-hide">
          {locations.map((loc) => (
            <button
              key={loc.slug}
              onClick={() => handleLocationClick(loc.slug, loc.lat, loc.lng)}
              className={`w-full text-left px-5 py-3 flex items-center justify-between border-b border-border transition-all ${
                activeSlug === loc.slug
                  ? "bg-primary/10 border-l-4 border-l-primary-deep"
                  : "hover:bg-muted/50 border-l-4 border-l-transparent"
              }`}
            >
              <div className="flex items-center gap-2.5">
                {loc.isPrimary ? (
                  <span className="h-3 w-3 rounded-full bg-secondary shrink-0 ring-2 ring-secondary/30" />
                ) : (
                  <span className="h-2.5 w-2.5 rounded-full bg-primary-deep shrink-0" />
                )}
                <div>
                  <div className={`font-heading font-bold text-sm tracking-tight ${activeSlug === loc.slug ? "text-primary-deep" : "text-foreground"}`}>
                    {loc.name}, {loc.state}
                  </div>
                  <div className="text-muted-foreground text-[11px]">
                    {loc.region}{loc.isPrimary ? " · HQ" : ""}
                  </div>
                </div>
              </div>
              <Link
                to={`/locations/${loc.slug}`}
                onClick={(e) => e.stopPropagation()}
                className="text-xs font-semibold text-primary-deep hover:text-primary-deep/80 whitespace-nowrap"
              >
                View →
              </Link>
            </button>
          ))}
        </div>

        <div className="w-full lg:w-[65%] h-[400px] lg:h-[520px] relative" style={{ zIndex: 0 }}>
          <div ref={mapRef} className="w-full h-full" style={{ zIndex: 0 }} />
        </div>
      </div>
    </div>
  );
};

export default LocationMap;
